export const matchedJobs = [
  {
    id: 1,
    title: 'Foley & Environmental Audio',
    rate: '$46/hr',
    match: 'Matches your Foley specialty',
    domain: 'Audio',
    status: 'New',
    tier: 'Advanced',
    client: 'Confidential — Major Studio',
    duration: '3–4 weeks',
    teamSize: '6 annotators',
    startDate: 'April 7, 2026',
    overview: 'Annotate and classify foley sound effects across a library of 12,000+ clips used in post-production for film and episodic content. Work includes tagging surface materials, contact types, environmental context, and emotional valence.',
    responsibilities: [
      'Classify foley clips by primary action (footsteps, cloth movement, object interaction, environmental)',
      'Tag surface material properties (wood, concrete, gravel, carpet, metal) with sub-categories',
      'Annotate spatial characteristics — room size estimation, reverb profile, stereo positioning',
      'Flag edge cases where multiple foley layers overlap and document separation approach',
      'Maintain annotation consistency guide for team reference',
    ],
    requirements: [
      'Audio domain expertise with foley or post-production background',
      'Minimum 93% quality score on previous audio batches',
      'Familiarity with DAW waveform reading and spectral analysis',
      'Available for minimum 15 hours/week during job duration',
    ],
    whyYou: 'Your Foley specialty and 96.2% Craft Score make you an ideal fit. You\'ve annotated 3 similar batches in the past with consistently high precision on surface material classification.',
  },
  {
    id: 2,
    title: 'Ambient Soundscape Classification',
    rate: '$42/hr',
    match: 'Based on your recent work',
    domain: 'Audio',
    status: 'New',
    tier: 'Standard',
    client: 'AI Research Lab',
    duration: '2 weeks',
    teamSize: '4 annotators',
    startDate: 'April 14, 2026',
    overview: 'Classify and segment ambient environmental recordings for a generative audio model. Dataset includes urban, rural, indoor, and natural environments captured across 40+ locations.',
    responsibilities: [
      'Segment continuous ambient recordings into distinct sound events and background layers',
      'Classify environmental context (indoor/outdoor, urban/rural, time of day estimation)',
      'Tag dominant and secondary sound sources with confidence levels',
      'Annotate transitions between environments in continuous recordings',
      'Document classification rationale for ambiguous scenes',
    ],
    requirements: [
      'Experience with environmental audio or field recording annotation',
      'Strong ear for layered soundscapes with multiple simultaneous sources',
      'Minimum 90% quality score on audio domain work',
      'Available for minimum 10 hours/week',
    ],
    whyYou: 'Your recent work on environmental audio batches shows strong performance in multi-source identification. This job builds directly on that skill set.',
  },
  {
    id: 3,
    title: 'Speech-in-Noise Annotation',
    rate: '$48/hr',
    match: 'Recommended for you',
    domain: 'Audio',
    status: 'Closing soon',
    tier: 'Expert',
    client: 'Enterprise Voice AI',
    duration: '4 weeks',
    teamSize: '8 annotators',
    startDate: 'April 2, 2026',
    overview: 'High-precision annotation of speech segments embedded in noisy environments. This dataset trains voice recognition models to isolate speech from background noise in real-world conditions — restaurants, transit, outdoor events, offices.',
    responsibilities: [
      'Transcribe speech content with exact timestamps in noisy audio',
      'Classify noise type and intensity on a standardized scale',
      'Mark speech intelligibility scores at segment level (1-5 scale)',
      'Identify and tag speaker changes, overlapping speech, and non-speech vocalizations',
      'Flag segments where speech is genuinely unintelligible vs. difficult but recoverable',
    ],
    requirements: [
      'Proven accuracy on speech annotation tasks (minimum 95% quality)',
      'Experience with noisy/degraded audio environments',
      'Native-level English comprehension for speech transcription',
      'Available for minimum 20 hours/week — this is a high-priority batch',
    ],
    whyYou: 'This batch has the highest precision requirements in the current queue. Your 96.2% Craft Score and edge case flagging track record qualify you for this tier. Higher rate reflects the precision demands.',
  },
  // Completed jobs (for profile history)
  {
    id: 4,
    title: 'Studio Dialogue Cleanup',
    rate: '$44/hr',
    match: 'Completed',
    domain: 'Audio',
    status: 'Completed',
    tier: 'Advanced',
    client: 'Streaming Platform',
    duration: '5 weeks',
    teamSize: '5 annotators',
    startDate: 'February 3, 2026',
    completedDate: 'March 10, 2026',
    overview: 'Clean-up annotation of studio-recorded dialogue for a major streaming platform\'s content library. Work involved identifying and tagging audio artifacts, background noise intrusions, mic handling noise, and room tone inconsistencies across 8,000+ dialogue clips.',
    responsibilities: [
      'Tag audio artifacts including plosives, sibilance, and mouth clicks with precise timestamps',
      'Identify background noise intrusions and classify by type (HVAC, traffic, crew movement)',
      'Mark mic handling noise and boom shadow audio with severity ratings',
      'Document room tone changes between takes for continuity assessment',
      'Flag clips requiring re-recording vs. those salvageable with post-processing',
    ],
    requirements: [
      'Experience with studio dialogue and post-production audio workflows',
      'Minimum 94% quality score on previous audio annotation tasks',
      'Familiarity with common recording artifacts and their causes',
      'Available for minimum 12 hours/week',
    ],
    whyYou: 'Completed with 96.8% quality score. Your ear for subtle artifacts was noted by the QA lead as exceptional.',
    quality: '96.8%',
    hoursLogged: '32h',
  },
  {
    id: 5,
    title: 'Automotive Sound Library',
    rate: '$42/hr',
    match: 'Completed',
    domain: 'Audio',
    status: 'Completed',
    tier: 'Standard',
    client: 'Automotive AI Startup',
    duration: '4 weeks',
    teamSize: '4 annotators',
    startDate: 'January 5, 2026',
    completedDate: 'February 1, 2026',
    overview: 'Classification and annotation of automotive sound recordings for an AI system that diagnoses vehicle issues by sound. Dataset included engine recordings, brake sounds, suspension noises, and exhaust signatures across 200+ vehicle models.',
    responsibilities: [
      'Classify engine sounds by type (combustion, electric, hybrid) and operating condition',
      'Tag anomalous sounds (knocking, squealing, grinding) with severity classification',
      'Annotate brake sound profiles including pad wear indicators and rotor contact patterns',
      'Document RPM-correlated sounds and speed-dependent noise patterns',
      'Create reference annotations for "healthy" vs. "needs attention" vehicle sounds',
    ],
    requirements: [
      'Audio annotation experience with mechanical or industrial sounds',
      'Ability to distinguish subtle tonal differences in repetitive sound patterns',
      'Minimum 90% quality score',
      'Available for minimum 10 hours/week',
    ],
    whyYou: 'Completed with 95.1% quality. Strong performance on mechanical sound classification despite being outside your primary Foley specialty.',
    quality: '95.1%',
    hoursLogged: '28h',
  },
  {
    id: 6,
    title: 'Nature Ambience Tagging',
    rate: '$40/hr',
    match: 'Completed',
    domain: 'Audio',
    status: 'Completed',
    tier: 'Standard',
    client: 'Meditation App',
    duration: '3 weeks',
    teamSize: '3 annotators',
    startDate: 'December 8, 2025',
    completedDate: 'January 2, 2026',
    overview: 'Annotation of nature and ambient recordings for a meditation and wellness application. Focus on granular tagging of natural sound elements — water, wind, birdsong, insects — with mood and intensity metadata for algorithmic soundscape generation.',
    responsibilities: [
      'Tag individual natural sound elements (water flow types, wind intensity, specific bird species)',
      'Annotate mood valence (calming, energizing, neutral) for each sound segment',
      'Mark intensity levels and dynamic range characteristics',
      'Identify and flag human-made sound intrusions (aircraft, distant traffic)',
      'Create segment-level metadata for algorithmic mixing compatibility',
    ],
    requirements: [
      'Experience with environmental or nature audio',
      'Good ear for subtle natural sound variations',
      'Minimum 90% quality score',
      'Available for minimum 8 hours/week',
    ],
    whyYou: 'Completed with 94.7% quality. Your environmental audio skills from Foley work translated well to nature sound classification.',
    quality: '94.7%',
    hoursLogged: '18h',
  },
  {
    id: 7,
    title: 'Podcast Speech Segmentation',
    rate: '$43/hr',
    match: 'Completed',
    domain: 'Audio',
    status: 'Completed',
    tier: 'Advanced',
    client: 'Podcast Platform',
    duration: '5 weeks',
    teamSize: '6 annotators',
    startDate: 'November 3, 2025',
    completedDate: 'December 5, 2025',
    overview: 'Speaker diarization and speech segmentation for a major podcast platform. Annotating speaker turns, cross-talk, laughter, filler words, and topic boundaries across 500+ podcast episodes for automated transcription and chapter generation.',
    responsibilities: [
      'Mark speaker turn boundaries with sub-second precision',
      'Tag cross-talk segments and identify dominant speaker during overlaps',
      'Annotate non-speech vocalizations (laughter, sighs, filler words) by speaker',
      'Identify topic change boundaries for automated chapter generation',
      'Flag segments with poor recording quality or unintelligible speech',
    ],
    requirements: [
      'Speech annotation experience with multi-speaker audio',
      'Strong attention to temporal precision in segmentation',
      'Minimum 93% quality score on speech tasks',
      'Available for minimum 15 hours/week',
    ],
    whyYou: 'Completed with 96.2% quality. Highest quality score on the team for speaker turn precision.',
    quality: '96.2%',
    hoursLogged: '36h',
  },
  {
    id: 8,
    title: 'Film Score Annotation',
    rate: '$45/hr',
    match: 'Completed',
    domain: 'Audio',
    status: 'Completed',
    tier: 'Expert',
    client: 'Music AI Lab',
    duration: '4 weeks',
    teamSize: '4 annotators',
    startDate: 'October 6, 2025',
    completedDate: 'November 1, 2025',
    overview: 'Detailed annotation of film score compositions for a generative music AI. Tagging instrumentation, emotional arc, tempo changes, key modulations, and scene-sync cues across 300+ film score cues from various genres.',
    responsibilities: [
      'Identify and tag individual instruments and instrument groups in orchestral arrangements',
      'Annotate emotional arc (tension, release, triumph, melancholy) with timestamp markers',
      'Mark tempo changes, time signature shifts, and key modulations',
      'Tag scene-sync cues (hits, stingers, swells) with intended visual sync points',
      'Classify compositional style (romantic, minimalist, avant-garde, contemporary)',
    ],
    requirements: [
      'Music theory knowledge or formal training',
      'Experience with orchestral or film score analysis',
      'Minimum 94% quality score',
      'Available for minimum 10 hours/week',
    ],
    whyYou: 'Completed with 97.0% quality. Your highest-rated job — the QA team flagged your emotional arc annotations as reference-quality.',
    quality: '97.0%',
    hoursLogged: '22h',
  },
  {
    id: 9,
    title: 'Urban Noise Classification',
    rate: '$41/hr',
    match: 'Completed',
    domain: 'Audio',
    status: 'Completed',
    tier: 'Standard',
    client: 'Smart City Initiative',
    duration: '4 weeks',
    teamSize: '5 annotators',
    startDate: 'September 1, 2025',
    completedDate: 'October 3, 2025',
    overview: 'Classification of urban noise recordings for a smart city noise monitoring system. Tagging noise sources, measuring perceived loudness, and annotating temporal patterns across 10,000+ clips from sensor networks in 3 major cities.',
    responsibilities: [
      'Classify primary noise sources (traffic, construction, nightlife, sirens, aircraft)',
      'Tag secondary and background noise layers with relative loudness',
      'Annotate temporal patterns (continuous, intermittent, impulse, cyclic)',
      'Mark noise events with start/end timestamps and peak intensity',
      'Flag clips with sensor artifacts or calibration issues',
    ],
    requirements: [
      'Environmental audio or urban acoustics experience',
      'Ability to work with lower-quality sensor recordings',
      'Minimum 90% quality score',
      'Available for minimum 12 hours/week',
    ],
    whyYou: 'Completed with 93.5% quality. First job on the platform — solid performance that established your baseline.',
    quality: '93.5%',
    hoursLogged: '26h',
  },
];

export const crossTeamInsights = [
  {
    id: 1,
    text: 'For clips with intermittent background noise, tag the noise pattern separately from the foreground source. QA approval rate went up 8%.',
    author: 'Lena Park',
    authorId: 7,
    authorInitials: 'LP',
    team: 'Team Waveform',
    domain: 'Audio',
    upvotes: 14,
    time: '6h ago',
    replies: [
      { author: 'Sofia Herrera', authorId: 9, authorInitials: 'SH', text: 'This changed my workflow completely. Was getting 2-3% lower QA before.', upvotes: 6, time: '5h ago' },
      { author: 'Daniel Kim', authorId: 10, authorInitials: 'DK', text: 'Does this apply to music tracks with intermittent silence too?', upvotes: 3, time: '4h ago' },
      { author: 'Lena Park', authorId: 7, authorInitials: 'LP', text: 'Good question — for music I\'d treat silence differently since it\'s intentional. This is more for environmental/field recordings.', upvotes: 5, time: '3h ago' },
    ],
    tags: ['technique', 'QA-validated'],
  },
  {
    id: 2,
    text: 'When annotating reverb tails, mark the decay endpoint where the signal drops below ambient — not where you stop hearing it subjectively.',
    author: 'Marcus Liu',
    authorId: 8,
    authorInitials: 'ML',
    team: 'Team Waveform',
    domain: 'Audio',
    upvotes: 9,
    time: '1d ago',
    replies: [
      { author: 'James Okonkwo', authorId: 4, authorInitials: 'JO', text: 'This is the kind of precision that separates good from great annotations. Bookmarked.', upvotes: 4, time: '22h ago' },
    ],
    tags: ['edge-case', 'reverb'],
  },
  {
    id: 3,
    text: 'Has anyone developed a consistent approach for overlapping dialogue in café scenes? I keep going back and forth on whether to prioritize the louder speaker or the one facing the mic.',
    author: 'Rachel Torres',
    authorId: 11,
    authorInitials: 'RT',
    team: 'Team Waveform',
    domain: 'Audio',
    upvotes: 7,
    time: '2d ago',
    replies: [
      { author: 'Maya Chen', authorId: 1, authorInitials: 'MC', text: 'I go with dominant source first (usually louder), then layer. QA rubric supports this.', upvotes: 8, time: '2d ago' },
      { author: 'Kenji Watanabe', authorId: 2, authorInitials: 'KW', text: 'Agree with Maya. Also worth flagging it as an edge case — these are exactly the clips that need calibration.', upvotes: 5, time: '1d ago' },
    ],
    tags: ['discussion', 'edge-case'],
  },
];

export const careerSignals = [
  {
    id: 1,
    text: 'You\'ve maintained Precision above 97% for 4 consecutive weeks.',
    type: 'milestone',
  },
  {
    id: 2,
    text: '24 jobs completed. Annotators with 25+ jobs get priority access to $48/hr batches.',
    type: 'progress',
  },
  {
    id: 3,
    text: 'Your 8-week quality streak qualifies you for the next Contributor Spotlight.',
    type: 'opportunity',
  },
];

export const teamOpenings = [
  {
    id: 1,
    team: 'Team Resonance',
    domain: 'Music',
    note: 'Needs an audio-adjacent specialist for cross-domain sprint',
    members: 4,
    maxMembers: 6,
  },
];

// Maya's job history — single source of truth for Profile page
export const mayaJobHistory = [
  { id: 1, title: 'Foley & Environmental Audio', domain: 'Audio', rate: '$46/hr', status: 'Active', period: 'Mar 2026 — Present', quality: '97.3%', hoursLogged: '24h', tier: 'Advanced', client: 'Confidential — Major Studio' },
  { id: 4, title: 'Studio Dialogue Cleanup', domain: 'Audio', rate: '$44/hr', status: 'Completed', period: 'Feb 2026 — Mar 2026', quality: '96.8%', hoursLogged: '32h', tier: 'Advanced', client: 'Streaming Platform', completedDate: 'Mar 10, 2026' },
  { id: 5, title: 'Automotive Sound Library', domain: 'Audio', rate: '$42/hr', status: 'Completed', period: 'Jan 2026 — Feb 2026', quality: '95.1%', hoursLogged: '28h', tier: 'Standard', client: 'Automotive AI Startup', completedDate: 'Feb 1, 2026' },
  { id: 6, title: 'Nature Ambience Tagging', domain: 'Audio', rate: '$40/hr', status: 'Completed', period: 'Dec 2025 — Jan 2026', quality: '94.7%', hoursLogged: '18h', tier: 'Standard', client: 'Meditation App', completedDate: 'Jan 2, 2026' },
  { id: 7, title: 'Podcast Speech Segmentation', domain: 'Audio', rate: '$43/hr', status: 'Completed', period: 'Nov 2025 — Dec 2025', quality: '96.2%', hoursLogged: '36h', tier: 'Advanced', client: 'Podcast Platform', completedDate: 'Dec 5, 2025' },
  { id: 8, title: 'Film Score Annotation', domain: 'Audio', rate: '$45/hr', status: 'Completed', period: 'Oct 2025 — Nov 2025', quality: '97.0%', hoursLogged: '22h', tier: 'Expert', client: 'Music AI Lab', completedDate: 'Nov 1, 2025' },
  { id: 9, title: 'Urban Noise Classification', domain: 'Audio', rate: '$41/hr', status: 'Completed', period: 'Sep 2025 — Oct 2025', quality: '93.5%', hoursLogged: '26h', tier: 'Standard', client: 'Smart City Initiative', completedDate: 'Oct 3, 2025' },
];
